import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, MapPin, Send, Loader2, ArrowUpRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import Reveal from "@/components/Reveal";
import { OFFICE, IMAGES, SERVICES } from "@/lib/site";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyForm = { name: "", email: "", company: "", service: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const update = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Please tell us your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = "Enter a valid email address.";
    if (!form.message.trim()) next.message = "A short message helps us prepare.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        service: form.service || null,
        message: form.message.trim(),
      });
      toast.success("Message sent — we'll be in touch shortly.");
      setForm(emptyForm);
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const fieldBase =
    "bg-ink-800 border-white/10 text-white placeholder:text-white/30 h-12 rounded-xl focus-visible:ring-cyber focus-visible:border-cyber/50 transition-colors";

  return (
    <div data-testid="contact-page">
      {/* Header */}
      <section className="relative overflow-hidden border-b border-white/10 pt-40 pb-20">
        <div className="absolute inset-0 grid-lines opacity-60" aria-hidden="true" />
        <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10">
          <Reveal>
            <span className="overline text-cyber">Contact</span>
          </Reveal>
          <Reveal i={1}>
            <h1 data-testid="contact-heading" className="mt-6 max-w-4xl font-display text-5xl font-semibold leading-[0.95] tracking-tighter text-white sm:text-6xl lg:text-7xl">
              Let&apos;s start the <span className="text-cyber">loop</span>.
            </h1>
          </Reveal>
          <Reveal i={2}>
            <p className="mt-8 max-w-2xl font-body text-lg leading-relaxed text-white/55">
              Tell us about your project, your team and where you want to be. We
              read every message and reply personally.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Body */}
      <section className="mx-auto grid max-w-[1400px] gap-14 px-6 py-24 lg:grid-cols-2 lg:px-10">
        {/* Form */}
        <Reveal>
          <form onSubmit={onSubmit} data-testid="contact-form" className="flex flex-col gap-6" noValidate>
            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="name" className="overline mb-3 block text-white/50">Name *</label>
                <Input
                  id="name"
                  data-testid="contact-name"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  placeholder="Ada Wong"
                  className={fieldBase}
                />
                {errors.name && <p data-testid="error-name" className="mt-2 font-body text-xs text-red-400">{errors.name}</p>}
              </div>
              <div>
                <label htmlFor="email" className="overline mb-3 block text-white/50">Email *</label>
                <Input
                  id="email"
                  type="email"
                  data-testid="contact-email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  placeholder="you@company.com"
                  className={fieldBase}
                />
                {errors.email && <p data-testid="error-email" className="mt-2 font-body text-xs text-red-400">{errors.email}</p>}
              </div>
            </div>

            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="company" className="overline mb-3 block text-white/50">Company</label>
                <Input
                  id="company"
                  data-testid="contact-company"
                  value={form.company}
                  onChange={(e) => update("company", e.target.value)}
                  placeholder="Optional"
                  className={fieldBase}
                />
              </div>
              <div>
                <label className="overline mb-3 block text-white/50">Interested in</label>
                <Select value={form.service} onValueChange={(v) => update("service", v)}>
                  <SelectTrigger data-testid="contact-service" className={fieldBase}>
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent className="border-white/10 bg-ink-800 text-white">
                    {SERVICES.map((s) => (
                      <SelectItem key={s.key} value={s.title} data-testid={`service-option-${s.key}`}>
                        {s.title}
                      </SelectItem>
                    ))}
                    <SelectItem value="Something else">Something else</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label htmlFor="message" className="overline mb-3 block text-white/50">Message *</label>
              <Textarea
                id="message"
                data-testid="contact-message"
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                placeholder="Tell us what you're building..."
                rows={6}
                className="resize-none rounded-xl border-white/10 bg-ink-800 text-white placeholder:text-white/30 focus-visible:border-cyber/50 focus-visible:ring-cyber"
              />
              {errors.message && <p data-testid="error-message" className="mt-2 font-body text-xs text-red-400">{errors.message}</p>}
            </div>

            <button
              type="submit"
              data-testid="contact-submit"
              disabled={submitting}
              className="group inline-flex w-max items-center gap-3 rounded-full bg-cyber px-8 py-4 font-body text-sm font-medium text-ink-950 transition-colors duration-300 hover:bg-white disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                </>
              ) : (
                <>
                  Send message
                  <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                </>
              )}
            </button>
          </form>
        </Reveal>

        {/* Location / info */}
        <Reveal i={1} className="flex flex-col gap-6">
          <div className="relative overflow-hidden rounded-3xl border border-white/10">
            <img src={IMAGES.location} alt="Hong Kong buildings from above at night" className="h-[300px] w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-ink-950/50 to-transparent" aria-hidden="true" />
            <div className="absolute bottom-0 left-0 p-8">
              <span className="overline text-cyber">Our studio</span>
              <p className="mt-3 font-display text-2xl font-semibold tracking-tight text-white">Hung Hom, Kowloon</p>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="glossy gloss-top rounded-3xl p-7">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-cyber/30 bg-cyber/10 text-cyber shadow-[inset_0_1px_0_rgba(255,255,255,0.25)]">
                <MapPin className="h-5 w-5" />
              </span>
              <h3 className="mt-5 overline text-white/50">Address</h3>
              <address className="mt-3 space-y-0.5 font-body text-sm not-italic leading-relaxed text-white/70" data-testid="office-address">
                {OFFICE.lines.map((line) => (
                  <div key={line}>{line}</div>
                ))}
              </address>
            </div>

            <div className="glossy gloss-top rounded-3xl p-7">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-cyber/30 bg-cyber/10 text-cyber shadow-[inset_0_1px_0_rgba(255,255,255,0.25)]">
                <Mail className="h-5 w-5" />
              </span>
              <h3 className="mt-5 overline text-white/50">Email</h3>
              <a
                href={`mailto:${OFFICE.email}`}
                data-testid="office-email"
                className="mt-3 inline-flex items-center gap-1.5 font-mono text-sm text-cyber transition-colors duration-300 hover:text-white"
              >
                {OFFICE.email}
                <ArrowUpRight className="h-3.5 w-3.5" />
              </a>
              <p className="mt-6 font-body text-sm leading-relaxed text-white/45">
                We typically respond within one business day, {OFFICE.region} time.
              </p>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
